import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ScrollReveal from '@/components/base/ScrollReveal';

const FEATURES = [
  { key: 'heroFeature1', icon: 'ri-check-double-line' },
  { key: 'heroFeature2', icon: 'ri-check-double-line' },
  { key: 'heroFeature3', icon: 'ri-check-double-line' },
  { key: 'heroFeature4', icon: 'ri-check-double-line' },
];

const TRUST_BADGES = [
  { key: 'trust1', icon: 'ri-building-4-line' },
  { key: 'trust2', icon: 'ri-settings-3-line' },
  { key: 'trust3', icon: 'ri-customer-service-2-line' },
];

const KPI_CARDS = [
  { labelKey: 'heroKpiRoas', value: '5.2x', icon: 'ri-funds-line', color: 'border-l-[#E53935]', delay: '0s', top: '5%', right: '-5%' },
  { labelKey: 'heroKpiRevenue', value: '$124K', icon: 'ri-money-dollar-circle-line', color: 'border-l-[#C8A24A]', delay: '0.6s', top: '42%', right: '-10%' },
  { labelKey: 'heroKpiLeads', value: '438', icon: 'ri-user-add-line', color: 'border-l-[#E53935]', delay: '1.2s', top: '60%', left: '-8%' },
  { labelKey: 'heroKpiGrowth', value: '+28%', icon: 'ri-line-chart-line', color: 'border-l-[#C8A24A]', delay: '1.8s', bottom: '8%', right: '2%' },
];

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center bg-white overflow-hidden pt-24 md:pt-28">
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-bl from-[#E53935]/5 via-[#C8A24A]/5 to-transparent rounded-full blur-3xl -translate-y-1/4 translate-x-1/4 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#C8A24A]/5 via-transparent to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none"></div>

      <div className="relative w-full max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <ScrollReveal>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F7F8FA] text-[#111111] text-xs font-semibold tracking-wider uppercase mb-6 border border-gray-100">
                <span className="w-2 h-2 rounded-full bg-[#E53935] animate-pulse-glow"></span>
                {t('trafficMaster.badge')}
              </span>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#111111] leading-[1.08] mb-5 tracking-tight">
                {t('trafficMaster.heroTitle')}
                <span className="block text-[#C8A24A] text-2xl md:text-3xl lg:text-4xl mt-2 font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  {t('trafficMaster.heroHeadline')}
                </span>
              </h1>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-3 max-w-xl">
                {t('trafficMaster.heroSubtitle')}
              </p>
              <p className="text-[#111111] text-sm font-semibold mb-7 max-w-xl border-l-2 border-[#C8A24A] pl-4 italic">
                {t('trafficMaster.heroSubtitle2')}
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-8">
                {FEATURES.map((f) => (
                  <div key={f.key} className="flex items-center gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-[#E53935]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${f.icon} text-[#E53935] text-xs`}></i>
                    </div>
                    <span className="text-[#111111] text-sm font-medium">{t(`trafficMaster.${f.key}`)}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.25}>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-8">
                <a
                  href="#final-cta"
                  className="px-7 py-4 rounded-full bg-[#E53935] text-white text-sm font-semibold hover:bg-[#B71C1C] transition-all duration-300 whitespace-nowrap shadow-[0_4px_20px_rgba(229,57,53,0.35)] hover:shadow-[0_6px_30px_rgba(229,57,53,0.45)] hover:-translate-y-0.5 inline-flex items-center gap-2 cursor-pointer"
                >
                  {t('trafficMaster.ctaPrimary')}
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#services"
                  className="px-7 py-4 rounded-full border-2 border-gray-200 text-[#111111] text-sm font-semibold hover:border-[#C8A24A] hover:text-[#C8A24A] transition-all duration-300 whitespace-nowrap inline-flex items-center gap-2 cursor-pointer"
                >
                  {t('trafficMaster.ctaSecondary')}
                  <i className="ri-arrow-down-line"></i>
                </a>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap items-center gap-6">
                {TRUST_BADGES.map((tb) => (
                  <div key={tb.key} className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-[#C8A24A]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${tb.icon} text-[#C8A24A] text-xs`}></i>
                    </div>
                    <span className="text-gray-500 text-xs font-medium">{t(`trafficMaster.${tb.key}`)}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>

          <div className="relative hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#E53935]/5 via-[#C8A24A]/5 to-transparent rounded-3xl blur-xl pointer-events-none"></div>

              <div className="relative z-10 rounded-2xl overflow-hidden shadow-[0_25px_70px_rgba(0,0,0,0.08)] border border-gray-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20laptop%20displaying%20Meta%20Ads%20digital%20marketing%20analytics%20dashboard%20with%20campaign%20performance%20charts%20conversion%20metrics%20ROAS%20data%20on%20screen%20alongside%20a%20smartphone%20with%20active%20campaigns%20clean%20modern%20minimalist%20workspace%20with%20soft%20warm%20lighting%20luxury%20agency%20aesthetic%20white%20background%20with%20subtle%20red%20and%20gold%20accents%20editorial%20product%20photography%20elegant%20composition&width=800&height=560&seq=traffic-master-hero-dashboard-01&orientation=landscape"
                  alt="Traffic Master Growth Dashboard"
                  className="w-full h-auto"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/20 via-transparent to-transparent pointer-events-none"></div>
              </div>

              {KPI_CARDS.map((kpi) => (
                <div
                  key={kpi.labelKey}
                  className="absolute z-20 bg-white/90 backdrop-blur-xl border border-gray-100 rounded-xl px-4 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.06)] animate-float border-l-[3px]"
                  style={{
                    animationDelay: kpi.delay,
                    animationDuration: '5.5s',
                    ...(kpi.top !== undefined ? { top: kpi.top } : {}),
                    ...(kpi.right !== undefined ? { right: kpi.right } : {}),
                    ...(kpi.bottom !== undefined ? { bottom: kpi.bottom } : {}),
                    ...(kpi.left !== undefined ? { left: kpi.left } : {}),
                  }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${kpi.icon} text-[#111111] text-sm`}></i>
                    <span className="text-gray-500 text-[10px] uppercase tracking-wider font-semibold">
                      {t(`trafficMaster.${kpi.labelKey}`)}
                    </span>
                  </div>
                  <span className="font-heading text-xl font-bold text-[#111111] block">
                    {kpi.value}
                  </span>
                </div>
              ))}

              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-[#C8A24A]/10 rounded-full blur-3xl pointer-events-none animate-pulse-glow"></div>
              <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#E53935]/10 rounded-full blur-3xl pointer-events-none animate-pulse-glow" style={{ animationDelay: '1.5s' }}></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}